import { useT } from "@transifex/react";
import { GlobeSimpleIcon } from "@phosphor-icons/react";
import { PhosphorIcon } from "../../basic/phosphorIcon/PhosphorIcon";
import PhaseSixIcon from "../../basic/phaseSixIcon/PhaseSixIcon";
import { SchoolClassVisibilities } from "../../enums/schoolClasses";
import { ClassActivityCategory, selectedCardStyles } from "../../constants/ClassActivityConstants";
import useBreakpoints, { Breakpoint } from "../../hooks/useBreakpoints";

interface ClassVisibilitySelectorProps {
    selectedVisibility: SchoolClassVisibilities;
    onVisibilityChange: (visibility: SchoolClassVisibilities) => void;

    /** Drives the selected-card accent color. Defaults to TEST (orange). */
    classActivityCategory?: ClassActivityCategory;
}

const ClassVisibilitySelector = (props: ClassVisibilitySelectorProps) => {
    const t = useT();
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const t_publicTitle = t("Public", { _tags: "SchoolClasses, ClassVisibilitySelector" });
    const t_publicSubtitle = t("Anyone with a link has access.", { _tags: "SchoolClasses, ClassVisibilitySelector" });
    const t_publicFeature = t("Vocabulary trainings & exercises", { _tags: "SchoolClasses, ClassVisibilitySelector" });
    const t_privateTitle = t("Private", { _tags: "SchoolClasses, ClassVisibilitySelector" });
    const t_privateSubtitle = t("Only your students have access.", { _tags: "SchoolClasses, ClassVisibilitySelector" });
    const t_privateFeature = t("Vocabulary trainings, exercises & tests", {
        _tags: "SchoolClasses, ClassVisibilitySelector",
    });

    const selectedStyle = selectedCardStyles[props.classActivityCategory ?? ClassActivityCategory.TEST];

    const renderCard = (visibility: SchoolClassVisibilities) => {
        const isSelected = props.selectedVisibility === visibility;
        const isPublic = visibility === SchoolClassVisibilities.PUBLIC;

        const borderClass = isSelected ? selectedStyle : "border border-p6-grey-light bg-white";

        return (
            <button
                type="button"
                aria-pressed={isSelected}
                data-testid={`class-visibility-option-${visibility}`}
                className={`${borderClass} rounded-medium cursor-pointer transition-colors overflow-clip p-m`}
                onClick={() => props.onVisibilityChange(visibility)}
            >
                <div className="flex flex-col items-center px-m">
                    <div className="flex flex-col gap-xs items-center pb-s border-b border-p6-grey-light w-full">
                        <div className="flex gap-xs items-center justify-center w-full">
                            {isPublic ? (
                                <PhosphorIcon
                                    phosphorIcon={GlobeSimpleIcon}
                                    fontSize="text-xxl"
                                    color="text-p6-grey-dark"
                                />
                            ) : (
                                <PhaseSixIcon
                                    name="lock"
                                    className="text-xxl text-p6-grey-dark"
                                />
                            )}
                            <span className="text-xl text-p6-grey-dark whitespace-nowrap">
                                {isPublic ? t_publicTitle : t_privateTitle}
                            </span>
                        </div>
                        <p className="text-l text-p6-grey-dark text-center w-full">
                            {isPublic ? t_publicSubtitle : t_privateSubtitle}
                        </p>
                    </div>
                    <div className="pt-s w-full">
                        <p className="text-l text-p6-grey-dark text-center">
                            {isPublic ? t_publicFeature : t_privateFeature}
                        </p>
                    </div>
                </div>
            </button>
        );
    };

    return (
        <div className={`grid gap-s ${getStyleBasedOnBreakpoint(Breakpoint.SM, "grid-cols-2", "grid-cols-1")}`}>
            {renderCard(SchoolClassVisibilities.PUBLIC)}
            {renderCard(SchoolClassVisibilities.PRIVATE)}
        </div>
    );
};

export default ClassVisibilitySelector;
